// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


const STATUS_STYLES = {
  draft: { bg: '#fdf6e3', fg: '#b7870a', border: '#f0d68a', label: 'Draft' },
  issued: { bg: '#e8f0fb', fg: '#2f5496', border: '#bcd4ee', label: 'Issued' },
  partial: { bg: '#fdf2e7', fg: '#d68910', border: '#f5c98e', label: 'Partial' },
  paid: { bg: '#e8f7ef', fg: '#1a7a5e', border: '#b8e3cf', label: 'Paid' },
  voided: { bg: '#f0f0f0', fg: '#888', border: '#dcdcdc', label: 'Voided' }
};

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 };
const td = { padding: '10px 12px', fontFamily: 'Arial, sans-serif', fontSize: 13 };

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      fontFamily: 'Arial, sans-serif', display: 'inline-block', whiteSpace: 'nowrap'
    }}>{s.label}</span>);

}

function fmtMoney(n) {
  return '$' + (parseFloat(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDate(s) {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-').map(Number);
  if (d.length !== 3) return s;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d[1] - 1]} ${d[2]}, ${d[0]}`;
}

export default function AdminInvoicesPage() {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    adminApi.listClients().then(setClients).catch(() => {});
    load();
  }, []);

  async function load() {
    setLoading(true);setError('');
    try {
      const params = {};
      if (filterClientId) params.clientId = filterClientId;
      if (filterStatus) params.status = filterStatus;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const data = await adminApi.listInvoices(params);
      setInvoices(data);
    } catch (err) {setError(err.message);} finally
    {setLoading(false);}
  }

  function clearFilters() {
    setFilterClientId('');setFilterStatus('');setFilterFrom('');setFilterTo('');
    setTimeout(load, 0);
  }

  // Aggregate stats
  const stats = invoices.reduce((acc, inv) => {
    const total = parseFloat(inv.total) || 0;
    const balance = parseFloat(inv.balance_due) || 0;
    if (inv.status === 'draft') acc.draftCount++;
    if (inv.status === 'issued' || inv.status === 'partial') {
      acc.outstandingCount++;
      acc.outstandingTotal += balance;
    }
    return acc;
  }, { draftCount: 0, outstandingCount: 0, outstandingTotal: 0 });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="page-subtitle">Manage all firm invoices. Generate new invoices from time entries and expenses that haven't been invoiced yet.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/dashboard/invoices/new')}>
          + Generate Invoice
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ margin: 0, textAlign: 'center', padding: 14 }}>
          <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', color: '#0F1838' }}>{invoices.length}</div>
          <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>Total Invoices</div>
        </div>
        <div className="card" style={{ margin: 0, textAlign: 'center', padding: 14 }}>
          <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', color: '#b7870a' }}>{stats.draftCount}</div>
          <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>Drafts</div>
        </div>
        <div className="card" style={{ margin: 0, textAlign: 'center', padding: 14 }}>
          <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', color: '#2f5496' }}>{stats.outstandingCount}</div>
          <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>Outstanding</div>
        </div>
        <div className="card" style={{ margin: 0, textAlign: 'center', padding: 14 }}>
          <div style={{ fontSize: 22, fontFamily: 'Arial, sans-serif', color: '#0F1838', fontWeight: 600 }}>{fmtMoney(stats.outstandingTotal)}</div>
          <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>Total Outstanding</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ fontSize: 13, marginBottom: 10 }}>Filters</div>
        <div className="form-row">
          <div className="form-group">
            <label>Client</label>
            <select value={filterClientId} onChange={(e) => setFilterClientId(e.target.value)}>
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: '0 0 160px' }}>
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="issued">Issued</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="voided">Voided</option>
            </select>
          </div>
          <div className="form-group" style={{ flex: '0 0 150px' }}>
            <label>From</label>
            <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '0 0 150px' }}>
            <label>To</label>
            <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
          </div>
        </div>
        <div className="btn-row">
          <button className="btn btn-sm btn-primary" onClick={load}>Apply</button>
          <button className="btn btn-sm" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      {/* Table */}
      {loading ?
      <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div> :
      invoices.length === 0 ?
      <div className="card" style={{ textAlign: 'center', color: '#888', padding: 32, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
          No invoices yet. Click "Generate Invoice" to create your first one.
        </div> :

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0F1838' }}>
                  <th style={{ ...th, width: 90 }}>Invoice #</th>
                  <th style={th}>Client</th>
                  <th style={{ ...th, width: 100 }}>Status</th>
                  <th style={{ ...th, width: 110 }}>Issue Date</th>
                  <th style={{ ...th, width: 110 }}>Due Date</th>
                  <th style={{ ...th, width: 110, textAlign: 'right' }}>Total</th>
                  <th style={{ ...th, width: 110, textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) =>
              <tr key={inv.id}
              onClick={() => navigate(`/admin/dashboard/invoices/${inv.id}`)}
              style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...td, fontWeight: 600 }}>#{inv.invoice_number}</td>
                    <td style={td}>{inv.client_name}</td>
                    <td style={td}><StatusBadge status={inv.status} /></td>
                    <td style={{ ...td, color: '#555' }}>{fmtDate(inv.issue_date)}</td>
                    <td style={{ ...td, color: '#555' }}>{fmtDate(inv.due_date)}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(inv.total)}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: parseFloat(inv.balance_due) > 0 ? 600 : 400, color: parseFloat(inv.balance_due) > 0 ? '#0F1838' : '#888' }}>
                      {parseFloat(inv.balance_due) > 0 ? fmtMoney(inv.balance_due) : '—'}
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      }
    </div>);

}